/**
 * Realized P&L export helpers — CSV + PDF.
 *
 * Both consume the payload from GET /api/portfolio/realized
 *   { base_currency, total_realized, securities_realized, assets_realized, events[] }
 * and a translate fn `t` + `lang` so the report is bilingual.
 */
import { formatMoney } from "./utils";

function eventDetail(ev, t) {
  if (ev.source === "security") {
    return `${t("realized_sold")} ${ev.quantity} × ${formatMoney(ev.price, ev.currency, { decimals: 2 })}`;
  }
  if (ev.asset_type === "precious_metal") {
    return `${ev.quantity} oz × ${formatMoney(ev.price, ev.currency, { decimals: 2 })}/oz`;
  }
  if (ev.mortgage_payoff) {
    return `${t("assets_mortgage_payoff")} −${formatMoney(ev.mortgage_payoff, ev.currency, { decimals: 0 })}`;
  }
  return t("realized_asset_sale");
}

function kindLabel(ev, t) {
  return ev.source === "security" ? t("realized_security") : t("realized_asset");
}

function fmtDate(d, lang) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}

function fileStamp() {
  return new Date().toISOString().slice(0, 10);
}

function safeName(s) {
  return (s || "portfolio").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase();
}

/** Build a CSV string and trigger a browser download. */
export function exportRealizedCsv(data, entity, t, lang, periodLabel) {
  const base = data.base_currency || "EUR";
  const headers = [
    t("realized_col_date"),
    t("realized_col_kind"),
    t("realized_col_name"),
    t("realized_col_detail"),
    `${t("realized_col_proceeds")} (${base})`,
    `${t("realized_col_gain")} (${base})`,
  ];
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = (data.events || []).map((ev) => [
    (ev.date || "").slice(0, 10),
    kindLabel(ev, t),
    ev.name || ev.symbol || "",
    eventDetail(ev, t).replace(/\u2212/g, "-"),
    ev.proceeds != null ? Number(ev.proceeds).toFixed(2) : "",
    ev.gain_base != null ? Number(ev.gain_base).toFixed(2) : "",
  ]);
  const totalRow = ["", "", t("realized_total").replace(/[:：]/g, "").trim(), "", "", Number(data.total_realized || 0).toFixed(2)];
  const lines = [headers, ...rows, totalRow].map((r) => r.map(esc).join(","));
  const csv = "\uFEFF" + lines.join("\n"); // BOM so Excel reads UTF-8 accents

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `realized_pnl_${safeName(entity?.name)}_${safeName(periodLabel) || "all"}_${fileStamp()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build a tidy PDF (jsPDF + autotable) and trigger a download. */
export async function exportRealizedPdf(data, entity, t, lang, periodLabel) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;
  const base = data.base_currency || "EUR";
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(t("realized_report_title"), marginX, 48);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(110);
  const period = periodLabel ? ` · ${periodLabel}` : "";
  const sub = `${entity?.name || ""}${period} · ${t("realized_generated_on")} ${fmtDate(new Date(), lang)}`;
  doc.text(sub, marginX, 66);

  // Totals line
  doc.setTextColor(40);
  const sgn = (v) => {
    const r = Math.round((Number(v) || 0) * 100) / 100;
    const s = r > 0 ? "+" : r < 0 ? "−" : "";
    return s + formatMoney(Math.abs(r), base, { decimals: 2 }).replace(/\u202f/g, " ");
  };
  const totals = `${t("realized_securities")}: ${sgn(data.securities_realized || 0)}    ${t("realized_assets")}: ${sgn(data.assets_realized || 0)}    ${t("realized_total").replace(/[:：]/g, "").trim()}: ${sgn(data.total_realized || 0)}`;
  doc.setFontSize(11);
  doc.text(totals, marginX, 88);

  const body = (data.events || []).map((ev) => [
    fmtDate(ev.date, lang),
    kindLabel(ev, t),
    ev.name || ev.symbol || "",
    eventDetail(ev, t).replace(/\u2212/g, "-"),
    ev.proceeds != null ? formatMoney(ev.proceeds, base, { decimals: 2 }).replace(/\u202f/g, " ") : "—",
    ev.gain_base != null ? sgn(ev.gain_base) : "—",
  ]);

  autoTable(doc, {
    startY: 104,
    margin: { left: marginX, right: marginX },
    head: [[
      t("realized_col_date"),
      t("realized_col_kind"),
      t("realized_col_name"),
      t("realized_col_detail"),
      `${t("realized_col_proceeds")} (${base})`,
      `${t("realized_col_gain")} (${base})`,
    ]],
    body,
    styles: { font: "helvetica", fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles: { 4: { halign: "right" }, 5: { halign: "right" } },
  });

  doc.save(`realized_pnl_${safeName(entity?.name)}_${safeName(periodLabel) || "all"}_${fileStamp()}.pdf`);
}
